import React, { useState } from "react";
import {
  HiSearch,
  HiGlobe,
  HiLocationMarker,
  HiServer,
  HiShieldCheck,
  HiClock,
  HiExclamationCircle,
} from "react-icons/hi";

const IPLookup = () => {
  const [ipAddress, setIPAddress] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const exampleAddresses = ["8.8.8.8", "1.1.1.1"];

  const handleLookup = async (ip = null) => {
    const targetIP = (ip || ipAddress).trim();
    if (!targetIP) return;

    setIsLoading(true);
    setError(null);
    setLookupResult(null);

    try {
      // Use ip-api.com free endpoint for IP geolocation data
      const response = await fetch(
        `http://ip-api.com/json/${targetIP}?fields=status,message,country,regionName,city,zip,lat,lon,timezone,isp,org,as,query`
      );
      const data = await response.json();

      if (data.status === "fail") {
        setError(data.message || "Failed to look up IP address");
      } else {
        setLookupResult({
          ip: data.query,
          country: data.country || "Unknown",
          region: data.regionName || "Unknown",
          city: data.city || "Unknown",
          zip: data.zip || "—",
          lat: data.lat,
          lon: data.lon,
          timezone: data.timezone || "—",
          isp: data.isp || "Unknown",
          org: data.org || "—",
          as: data.as || "—",
        });
      }
    } catch (err) {
      setError("Unable to reach IP lookup service. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLookup();
  };

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-10 mt-8">
        <h1 className="text-2xl font-bold text-white">IP Address Lookup</h1>
        <p className="text-gray-400 text-sm mt-2">
          Comprehensive IP analysis and security assessment for network
          administrators
        </p>
      </div>

      {/* Search Input */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="relative">
          <input
            type="text"
            value={ipAddress}
            onChange={(e) => setIPAddress(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter IP address for analysis (e.g., 8.8.8.8)"
            className="w-full bg-[#2A2A2A] border border-white/10 rounded-xl px-5 py-4 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#BDE958]/50 pr-12"
          />
          <button
            onClick={() => handleLookup()}
            disabled={isLoading || !ipAddress.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-lg bg-[#BDE958] text-[#161616] hover:bg-[#a8d44a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <HiSearch className="w-4 h-4" />
          </button>
        </div>

        {/* Example Addresses */}
        <div className="flex items-center justify-center gap-3 mt-4">
          <span className="text-gray-500 text-xs">Example addresses</span>
          {exampleAddresses.map((addr) => (
            <button
              key={addr}
              onClick={() => {
                setIPAddress(addr);
                handleLookup(addr);
              }}
              className="px-3 py-1 bg-[#2A2A2A] border border-white/10 rounded-md text-xs text-gray-300 hover:bg-white/5 hover:text-[#BDE958] transition-colors font-mono"
            >
              {addr}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#BDE958]"></div>
          <p className="text-gray-400 mt-4 text-sm">
            Analyzing IP address...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
            <HiExclamationCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Result */}
      {lookupResult && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#1E1E1E] rounded-xl border border-white/5 overflow-hidden">
            {/* IP Header */}
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
                  IP Address
                </p>
                <p className="text-xl font-bold text-white font-mono mt-1">
                  {lookupResult.ip}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-[#BDE958]/10 flex items-center justify-center">
                <HiGlobe className="w-5 h-5 text-[#BDE958]" />
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5">
              <div className="bg-[#1E1E1E] px-6 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <HiLocationMarker className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    Location
                  </span>
                </div>
                <p className="text-white text-sm font-medium">
                  {lookupResult.city}, {lookupResult.region}
                </p>
                <p className="text-gray-400 text-xs mt-0.5">
                  {lookupResult.country} • {lookupResult.zip}
                </p>
              </div>

              <div className="bg-[#1E1E1E] px-6 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <HiClock className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    Timezone
                  </span>
                </div>
                <p className="text-white text-sm font-medium">
                  {lookupResult.timezone}
                </p>
                <p className="text-gray-400 text-xs mt-0.5">
                  {lookupResult.lat}, {lookupResult.lon}
                </p>
              </div>

              <div className="bg-[#1E1E1E] px-6 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <HiServer className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    ISP
                  </span>
                </div>
                <p className="text-white text-sm font-medium">
                  {lookupResult.isp}
                </p>
                <p className="text-gray-400 text-xs mt-0.5">
                  {lookupResult.as}
                </p>
              </div>

              <div className="bg-[#1E1E1E] px-6 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <HiShieldCheck className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    Organization
                  </span>
                </div>
                <p className="text-white text-sm font-medium">
                  {lookupResult.org}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IPLookup;
